import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Plus,
  Trash2,
  Timer,
  Dumbbell,
  BookOpen,
  Search,
  X,
} from "lucide-react";
import api from "../../lib/api";
import { v4 as uuidv4 } from "uuid";
import { cn } from "../../lib/utils";

const DEFAULT_METRIC = "reps";

const METRIC_OPTIONS = [
  { value: "reps", label: "Reps" },
  { value: "duration", label: "Duration" },
  { value: "distance", label: "Distance" },
  { value: "count", label: "Count" },
];

function buildDefaultSet(metric) {
  const base = { id: uuidv4(), restSeconds: "none" };
  if (metric === "reps") return { ...base, reps: 10, weightKg: "" };
  if (metric === "duration") return { ...base, durationMin: 1 };
  if (metric === "distance") return { ...base, distanceKm: 1 };
  if (metric === "count") return { ...base, count: 10 };
  return base;
}

function SetRow({ set, index, metric, onChange, onRemove, canRemove }) {
  const update = (field, val) => onChange({ ...set, [field]: val });

  return (
    <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-2 border border-border/40">
      <span className="text-[10px] font-mono text-muted-foreground w-8 shrink-0">
        Set {index + 1}
      </span>
      {metric === "reps" && (
        <>
          <div className="flex flex-col gap-0.5 min-w-0">
            <Label className="text-[9px] font-mono uppercase text-muted-foreground">
              Reps
            </Label>
            <Input
              type="number"
              min={1}
              inputMode="numeric"
              pattern="[0-9]*"
              value={set.reps ?? 10}
              onChange={(e) => update("reps", Number(e.target.value))}
              className="h-7 w-16 text-xs text-center"
            />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <Label className="text-[9px] font-mono uppercase text-muted-foreground">
              Wt (kg)
            </Label>
            <Input
              type="number"
              step={0.5}
              inputMode="decimal"
              value={set.weightKg ?? ""}
              onChange={(e) => update("weightKg", e.target.value)}
              placeholder="—"
              className="h-7 w-16 text-xs text-center"
            />
          </div>
        </>
      )}
      {metric === "duration" && (
        <div className="flex flex-col gap-0.5">
          <Label className="text-[9px] font-mono uppercase text-muted-foreground">
            Min
          </Label>
          <Input
            type="number"
            min={0.5}
            step={0.5}
            inputMode="decimal"
            value={set.durationMin ?? 1}
            onChange={(e) => update("durationMin", Number(e.target.value))}
            className="h-7 w-16 text-xs text-center"
          />
        </div>
      )}
      {metric === "distance" && (
        <div className="flex flex-col gap-0.5">
          <Label className="text-[9px] font-mono uppercase text-muted-foreground">
            km
          </Label>
          <Input
            type="number"
            min={0.1}
            step={0.1}
            inputMode="decimal"
            value={set.distanceKm ?? 1}
            onChange={(e) => update("distanceKm", Number(e.target.value))}
            className="h-7 w-16 text-xs text-center"
          />
        </div>
      )}
      {metric === "count" && (
        <div className="flex flex-col gap-0.5">
          <Label className="text-[9px] font-mono uppercase text-muted-foreground">
            Count
          </Label>
          <Input
            type="number"
            min={1}
            inputMode="numeric"
            pattern="[0-9]*"
            value={set.count ?? 10}
            onChange={(e) => update("count", Number(e.target.value))}
            className="h-7 w-16 text-xs text-center"
          />
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <Label className="text-[9px] font-mono uppercase text-muted-foreground">
          Rest (s)
        </Label>
        <Input
          type="number"
          min={0}
          step={5}
          inputMode="numeric"
          pattern="[0-9]*"
          value={
            set.restSeconds === "none" ||
            set.restSeconds === undefined ||
            set.restSeconds === null ||
            set.restSeconds === 0
              ? ""
              : set.restSeconds
          }
          onChange={(e) => {
            const val = e.target.value === "" ? "none" : Number(e.target.value);
            update("restSeconds", val);
          }}
          placeholder="None"
          className="h-7 w-16 text-xs text-center"
        />
      </div>
      {canRemove && (
        <button
          onClick={onRemove}
          className="ml-auto text-destructive/60 hover:text-destructive transition-colors p-1"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * MemberSelfWorkoutDialog
 * Props:
 *   open, onClose, initialData, isStaff, onSave
 */
export default function MemberSelfWorkoutDialog({
  open,
  onClose,
  initialData = null,
  isStaff = false,
  onSave,
}) {
  const isEdit = !!initialData;
  const [name, setName] = useState("");
  const [metric, setMetric] = useState(DEFAULT_METRIC);
  const [sets, setSets] = useState([buildDefaultSet(DEFAULT_METRIC)]);
  const [enableTimer, setEnableTimer] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);

  // Search state
  const [query, setQuery] = useState("");
  const [gymResults, setGymResults] = useState([]);
  const [memberResults, setMemberResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [addingToLib, setAddingToLib] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Reset when dialog opens/closes or initialData changes
  useEffect(() => {
    if (open) {
      if (initialData) {
        setName(initialData.title || "");
        setQuery(initialData.title || "");
        setMetric(initialData.workoutMetric || DEFAULT_METRIC);
        setSets(
          initialData.setDetails?.length
            ? initialData.setDetails.map((s) => ({
                ...s,
                id: s.id || uuidv4(),
              }))
            : [buildDefaultSet(initialData.workoutMetric || DEFAULT_METRIC)],
        );
        setEnableTimer(initialData.enableTimer || false);
        setInstructions(initialData.instructions || "");
      } else {
        setName("");
        setQuery("");
        setMetric(DEFAULT_METRIC);
        setSets([buildDefaultSet(DEFAULT_METRIC)]);
        setEnableTimer(false);
        setInstructions("");
      }
      setGymResults([]);
      setMemberResults([]);
      setShowDropdown(false);
    }
  }, [open, initialData]);

  // Debounce search
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setGymResults([]);
      setMemberResults([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    try {
      const token = localStorage.getItem("member_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await api.get("/member-portal/workouts/search", {
        params: { q: q.trim() },
        headers,
      });
      setGymResults(res.data?.gymWorkouts || []);
      setMemberResults(res.data?.memberWorkouts || []);
      setShowDropdown(true);
    } catch {
      setGymResults([]);
      setMemberResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQueryChange = (val) => {
    setQuery(val);
    setName(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const selectWorkout = (workout) => {
    setName(workout.title || "");
    setQuery(workout.title || "");
    setMetric(workout.workoutMetric || DEFAULT_METRIC);
    setSets(
      workout.setDetails?.length
        ? workout.setDetails.map((s) => ({ ...s, id: s.id || uuidv4() }))
        : [buildDefaultSet(workout.workoutMetric || DEFAULT_METRIC)],
    );
    setEnableTimer(workout.enableTimer || false);
    setInstructions(workout.instructions || "");
    setShowDropdown(false);
  };

  const handleMetricChange = (val) => {
    setMetric(val);
    setSets((prev) => prev.map(() => buildDefaultSet(val)));
  };

  const addSet = () => setSets((prev) => [...prev, buildDefaultSet(metric)]);
  const removeSet = (idx) =>
    setSets((prev) => prev.filter((_, i) => i !== idx));
  const updateSet = (idx, updated) =>
    setSets((prev) => prev.map((s, i) => (i === idx ? updated : s)));

  const handleAddToMyLib = async () => {
    if (!name.trim()) return;
    setAddingToLib(true);
    try {
      const token = localStorage.getItem("member_token");
      const res = await api.post(
        "/member-portal/member-workout-lib",
        {
          title: name.trim(),
          workoutMetric: metric,
          setDetails: sets,
          enableTimer,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      selectWorkout(
        res.data?.workout || {
          title: name,
          workoutMetric: metric,
          setDetails: sets,
          enableTimer,
        },
      );
    } catch {
      // silent fail — continue with the name as typed
    } finally {
      setAddingToLib(false);
      setShowDropdown(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const exercise = {
        id: initialData?.id || uuidv4(),
        workoutId: initialData?.workoutId || "",
        title: name.trim(),
        workoutMetric: metric,
        setDetails: sets,
        sets: sets.length,
        enableTimer,
        instructions: isStaff ? instructions : initialData?.instructions || "",
      };
      await onSave(exercise);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const totalResults = gymResults.length + memberResults.length;
  const nameNotInResults =
    query.trim().length > 1 &&
    !gymResults.some(
      (w) => w.title?.toLowerCase() === query.trim().toLowerCase(),
    ) &&
    !memberResults.some(
      (w) => w.title?.toLowerCase() === query.trim().toLowerCase(),
    );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        className="bg-card border-border max-w-lg w-[95%] max-h-[90vh] flex flex-col rounded-xl p-0 overflow-hidden top-4 sm:top-[5%] translate-y-0"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/50 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold font-heading uppercase tracking-wide">
            <Dumbbell className="h-4 w-4 text-primary" />
            {isEdit ? "Edit Exercise" : "Add Exercise"}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-0 space-y-4 flex-1 min-h-0 overflow-y-auto">
          {/* Name / Search */}
          <div className="relative" ref={searchRef}>
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Exercise Name *
            </Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                placeholder="Search or type a name..."
                className="pl-8 pr-8 h-9 text-sm"
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery("");
                    setName("");
                    setShowDropdown(false);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Dropdown */}
            {showDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-xl overflow-hidden max-h-56 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                {searching && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    Searching…
                  </div>
                )}
                {!searching && totalResults === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    No results
                  </div>
                )}
                {gymResults.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[9px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary/30 flex items-center gap-1">
                      <Dumbbell className="h-3 w-3" /> Gym Library
                    </div>
                    {gymResults.map((w) => {
                      const setsCount = w.sets || w.setDetails?.length || 0;
                      const metric = w.workoutMetric || "no metric";

                      return (
                        <button
                          key={w.id}
                          onClick={() => selectWorkout(w)}
                          className="w-full px-3 py-2 hover:bg-secondary/40 text-left transition-colors border-b border-border/20 last:border-0 block"
                        >
                          <div className="font-semibold text-sm text-foreground truncate flex items-center justify-between">
                            <span>{w.title}</span>
                            {w.isPreset && (
                              <span className="text-[9px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-1 rounded ml-2 select-none uppercase font-bold">
                                Preset
                              </span>
                            )}
                          </div>

                          {w.isPreset && w.presetName && (
                            <div className="text-xs text-amber-500/90 font-medium mt-0.5 truncate">
                              {w.presetName}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1 mt-1">
                            {w.groupNames?.[0] && (
                              <span className="bg-primary/10 text-primary border border-primary/20 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                {w.groupNames[0]}
                              </span>
                            )}
                            {w.workoutType && (
                              <span className="bg-secondary text-secondary-foreground border border-border/50 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                {w.workoutType}
                              </span>
                            )}
                          </div>

                          <div className="text-[10px] text-muted-foreground mt-1 truncate">
                            <span className="font-medium text-foreground">
                              {setsCount} set{setsCount !== 1 ? "s" : ""} (
                              {metric})
                            </span>
                            {(w.shortDescription || w.instructions) && (
                              <span className="opacity-80">
                                {" • "}
                                {w.shortDescription || w.instructions}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
                {memberResults.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[9px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary/30 flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> My Library
                    </div>
                    {memberResults.map((w) => {
                      const setsCount = w.sets || w.setDetails?.length || 0;
                      const metric = w.workoutMetric || "no metric";

                      return (
                        <button
                          key={w.id}
                          onClick={() => selectWorkout(w)}
                          className="w-full px-3 py-2 hover:bg-secondary/40 text-left transition-colors border-b border-border/20 last:border-0 block"
                        >
                          <div className="font-semibold text-sm text-foreground truncate flex items-center justify-between">
                            <span>{w.title}</span>
                            {w.isPreset && (
                              <span className="text-[9px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-1 rounded ml-2 select-none uppercase font-bold">
                                Preset
                              </span>
                            )}
                          </div>

                          {w.isPreset && w.presetName && (
                            <div className="text-xs text-amber-500/90 font-medium mt-0.5 truncate">
                              {w.presetName}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1 mt-1">
                            {w.groupNames?.[0] && (
                              <span className="bg-primary/10 text-primary border border-primary/20 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                {w.groupNames[0]}
                              </span>
                            )}
                            {w.workoutType && (
                              <span className="bg-secondary text-secondary-foreground border border-border/50 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                {w.workoutType}
                              </span>
                            )}
                          </div>

                          <div className="text-[10px] text-muted-foreground mt-1 truncate">
                            <span className="font-medium text-foreground">
                              {setsCount} set{setsCount !== 1 ? "s" : ""} (
                              {metric})
                            </span>
                            {(w.shortDescription || w.instructions) && (
                              <span className="opacity-80">
                                {" • "}
                                {w.shortDescription || w.instructions}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
                {nameNotInResults && !searching && (
                  <button
                    onClick={handleAddToMyLib}
                    disabled={addingToLib}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-emerald-500/10 text-left transition-colors border-t border-border/40"
                  >
                    <Plus className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                      {addingToLib
                        ? "Saving…"
                        : `Add "${query.trim()}" to My Library`}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Metric Type */}
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Metric Type
            </Label>
            <Select value={metric} onValueChange={handleMetricChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-sm">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sets */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Sets ({sets.length})
              </Label>
              <button
                onClick={addSet}
                className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 font-medium transition-colors"
              >
                <Plus className="h-3 w-3" /> Add Set
              </button>
            </div>
            <div className="space-y-1.5">
              {sets.map((set, idx) => (
                <SetRow
                  key={set.id || idx}
                  set={set}
                  index={idx}
                  metric={metric}
                  onChange={(updated) => updateSet(idx, updated)}
                  onRemove={() => removeSet(idx)}
                  canRemove={sets.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Enable Timer */}
          <div className="flex items-center justify-between py-2 border-t border-border/40 hidden">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-foreground">
                Enable Timer
              </span>
              <span className="text-[10px] text-muted-foreground">
                Start timer when working on this exercise
              </span>
            </div>
            <Switch checked={enableTimer} onCheckedChange={setEnableTimer} />
          </div>

          {/* Notes / Instructions (staff only) */}
          {isStaff && (
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
                Notes / Instructions (optional)
              </Label>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Add any technique notes or instructions..."
                rows={3}
                className="text-sm resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3.5 border-t border-border/50 flex gap-2 justify-end shrink-0 flex-row">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs font-heading uppercase tracking-wide"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="text-xs font-heading uppercase tracking-wide min-w-[80px]"
          >
            {saving ? "Saving…" : isEdit ? "Update" : "Add Exercise"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
